import {
  UserRole,
  OrganizationRole,
  PermissionLevel,
  InterfacePermission,
  UserOrganization,
} from '@/types/user'
import { UserOrganizationRole } from '@/types/org'

/**
 * Interface Permission System Utilities
 * Implements the comprehensive permission logic as specified in the requirements
 */

/**
 * Determines the global permission level for a user
 */
export const getGlobalPermissionLevel = (
  user: UserOrganization,
): PermissionLevel => {
  if (!user) return PermissionLevel.NO_ACCESS

  // Step 1: Check global admin status
  if (user.role === UserRole.SuperAdmin || user.role === UserRole.Admin) {
    return PermissionLevel.FULL_ADMIN
  }

  return PermissionLevel.USER // Default for regular users, will be refined per interface
}

/**
 * Finds the intersection of user organizations and interface organizations
 */
export const findSharedOrganizations = (
  userOrganizations: UserOrganizationRole[],
  interfaceOrganizations: { id: number; name: string; description?: string }[],
): number[] => {
  if (!userOrganizations || !interfaceOrganizations) return []

  const userOrgIds = userOrganizations.map((org) => org.id)
  const interfaceOrgIds = interfaceOrganizations.map((org) => org.id)

  return userOrgIds.filter((id) => interfaceOrgIds.includes(id))
}

/**
 * Determines the highest role a user has in shared organizations
 */
export const getHighestRoleInSharedOrgs = (
  userOrganizations: UserOrganizationRole[],
  sharedOrgIds: number[],
): OrganizationRole | null => {
  if (!userOrganizations || !sharedOrgIds.length) return null

  const rolesInSharedOrgs = userOrganizations
    .filter((org) => sharedOrgIds.includes(org.id))
    .map((org) => org.role as any as OrganizationRole)

  // Priority ranking: Manager > User > ViewOnly
  if (rolesInSharedOrgs.includes(OrganizationRole.Manager)) {
    return OrganizationRole.Manager
  }
  if (rolesInSharedOrgs.includes(OrganizationRole.Member)) {
    return OrganizationRole.Member
  }
  if (rolesInSharedOrgs.includes(OrganizationRole.ViewOnly)) {
    return OrganizationRole.ViewOnly
  }

  return null
}

/**
 * Maps organization role to permission level
 */
export const mapRoleToPermissionLevel = (
  role: OrganizationRole | null,
  globalLevel: PermissionLevel,
): PermissionLevel => {
  // Global admins always get full access
  if (globalLevel === PermissionLevel.FULL_ADMIN) {
    return PermissionLevel.FULL_ADMIN
  }

  if (!role) return PermissionLevel.NO_ACCESS

  switch (role) {
    case OrganizationRole.Manager:
      return PermissionLevel.SUPER_USER
    case OrganizationRole.Member:
      return PermissionLevel.USER
    case OrganizationRole.ViewOnly:
      return PermissionLevel.VIEW_ONLY
    default:
      return PermissionLevel.NO_ACCESS
  }
}

/**
 * Calculates interface permission for a user
 */
export const calculateInterfacePermission = (
  user: UserOrganization,
  interfaceData: {
    id: number
    organizations: { id: number; name: string; description?: string }[]
  },
): InterfacePermission => {
  const globalLevel = getGlobalPermissionLevel(user)

  // Step 1: Global admin check
  if (globalLevel === PermissionLevel.FULL_ADMIN) {
    return {
      interfaceId: interfaceData.id,
      permissionLevel: PermissionLevel.FULL_ADMIN,
      canView: true,
      canEdit: true,
      canAccessAdmin: true,
      canManageUsers: true,
      sharedOrganizations: interfaceData.organizations.map((org) => org.id),
      highestRole: OrganizationRole.Manager,
    }
  }

  // Step 2: Find shared organizations
  const sharedOrgIds = findSharedOrganizations(
    user.organizations || [],
    interfaceData.organizations,
  )

  // Step 3: No shared organizations = no access
  if (!sharedOrgIds.length) {
    return {
      interfaceId: interfaceData.id,
      permissionLevel: PermissionLevel.NO_ACCESS,
      canView: false,
      canEdit: false,
      canAccessAdmin: false,
      canManageUsers: false,
      sharedOrganizations: [],
      highestRole: null,
    }
  }

  // Step 4: Get highest role in shared organizations
  const highestRole = getHighestRoleInSharedOrgs(
    user.organizations || [],
    sharedOrgIds,
  )
  const permissionLevel = mapRoleToPermissionLevel(highestRole, globalLevel)

  // Step 5: Map permission level to capabilities
  return {
    interfaceId: interfaceData.id,
    permissionLevel,
    canView: permissionLevel !== PermissionLevel.NO_ACCESS,
    canEdit: [
      PermissionLevel.FULL_ADMIN,
      PermissionLevel.SUPER_USER,
      PermissionLevel.USER,
    ].includes(permissionLevel),
    canAccessAdmin: [
      PermissionLevel.FULL_ADMIN,
      PermissionLevel.SUPER_USER,
    ].includes(permissionLevel),
    canManageUsers: [
      PermissionLevel.FULL_ADMIN,
      PermissionLevel.SUPER_USER,
    ].includes(permissionLevel),
    sharedOrganizations: sharedOrgIds,
    highestRole,
  }
}

/**
 * Filters interfaces based on user permissions
 */
export const filterInterfacesByPermission = (
  user: UserOrganization,
  interfaces: any[],
): any[] => {
  if (!user || !interfaces) return []

  const globalLevel = getGlobalPermissionLevel(user)

  // Global admins see all interfaces
  if (globalLevel === PermissionLevel.FULL_ADMIN) {
    return interfaces
  }

  // Filter interfaces where user has organizational access
  return interfaces.filter((interfaceItem) => {
    const sharedOrgIds = findSharedOrganizations(
      user.organizations || [],
      interfaceItem.organizations || [],
    )
    return sharedOrgIds.length > 0
  })
}

/**
 * Checks if user can access admin routes
 */
export const canAccessAdminRoutes = (user: UserOrganization): boolean => {
  const globalLevel = getGlobalPermissionLevel(user)

  if (globalLevel === PermissionLevel.FULL_ADMIN) {
    return true
  }

  // Check if user has SuperUser role in any organization
  if (user.organizations) {
    return user.organizations.some(
      (org) => (org.role as any) === OrganizationRole.Manager,
    )
  }

  return false
}

/**
 * Checks if user can manage other users
 */
export const canManageUsers = (user: UserOrganization): boolean => {
  return canAccessAdminRoutes(user) // Same logic as admin routes
}

/**
 * Gets permission level for a specific interface
 */
export const getInterfacePermissionLevel = (
  user: UserOrganization,
  interfaceData: any,
): PermissionLevel => {
  const permission = calculateInterfacePermission(user, interfaceData)
  return permission.permissionLevel
}
